import React, { useState, useEffect } from 'react'
import {
  Btn,
  CustomDropdown,
  CustomModal,
  InputNumeroInteiro,
  CustomInputTextArea,
} from '../../../../../components'
import { getEmissoresByTps } from '../../../../../services/api/https/settings/emissor-cte.http'
import {
  getDadosEmissaoCte,
  postEmitCTeGlobalizado,
} from '../../../../../services/api/https/deliveries/cte.http'
import { getCfops } from '../../../../../services/api/https/settings/cfop.http'
import ValoresFiscaisCte from '../ValoresFiscaisCte/ValoresFiscaisCte'
import {
  VALORES_FISCAIS_INICIAIS,
  erroNumeroOrdemColeta,
  erroValoresFiscais,
  montarComponentesValor,
  novoComponente,
} from '../ValoresFiscaisCte/valoresFiscaisCte.util'
import useFormularioCte from '../Partes/useFormularioCte'
import SecaoPartes from '../Partes/SecaoPartes'
import SecaoTomador from '../Partes/SecaoTomador'
import SecaoPrestacao from '../Partes/SecaoPrestacao'
import SecaoCarga from '../Partes/SecaoCarga'
import SecaoDocumentosCarga from '../Partes/SecaoDocumentosCarga'
import useEmissorUnico from '../Partes/useEmissorUnico'

const inicialState = {
  emissor_id: null,
  cfop_id: null,
  numero_ordem_coleta: null,
  ...VALORES_FISCAIS_INICIAIS,
  observacoes: null,
}

export default function ModalEmitirCteGlobalizado({
  selectedTp,
  visible,
  valorFrete,
  // Componentes do valor consolidados por regra entre todas as notas selecionadas
  componentes: componentesIniciais = null,
  deliveriesIds,
  onModalClose,
  onModalSave,
  onError,
}) {
  // Hooks
  const [isLoading, setIsLoading] = useState(false)
  const [state, setState] = useState(inicialState)
  const [componentes, setComponentes] = useState([])
  const [emissores, setEmissores] = useState([])
  const [cfops, setCfops] = useState([])
  const [documentos, setDocumentos] = useState([])
  const [carregandoDados, setCarregandoDados] = useState(false)
  const form = useFormularioCte({
    visible,
    onError,
    origemRemetente: 'empresa',
    destinatarioFixo: true,
  })
  const { aplicarDadosIniciais, definirFim } = form
  const chaveNotas = (deliveriesIds || []).join(',')

  // Atualizar valores de frete quando o modal abrir
  useEffect(() => {
    if (visible && valorFrete !== null) {
      setComponentes((componentesIniciais || []).map(c => novoComponente(c.nome, c.valor)))
      setState(prevState => ({
        ...prevState,
        valor_total: valorFrete,
        valor_receber: valorFrete,
        icms_base_calculo: valorFrete,
        ibs_cbs_base_calculo: valorFrete,
        observacoes: '',
      }))
    }
  }, [visible, valorFrete, componentesIniciais])

  useEffect(() => {
    let ativo = true
    async function fetchDados() {
      try {
        setCarregandoDados(true)
        const dados = await getDadosEmissaoCte(deliveriesIds, true)
        if (!ativo) return
        aplicarDadosIniciais(dados)
        setDocumentos(dados.documentos || [])
      } catch (error) {
        if (ativo) onError(error)
      } finally {
        if (ativo) setCarregandoDados(false)
      }
    }
    if (!visible || !deliveriesIds || !deliveriesIds.length) return undefined
    setDocumentos([])
    fetchDados()
    return () => {
      ativo = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, chaveNotas])

  useEffect(() => {
    async function fetchListas() {
      try {
        const [listaEmissores, listaCfops] = await Promise.all([
          getEmissoresByTps(selectedTp),
          getCfops(),
        ])
        setEmissores(listaEmissores)
        setCfops(
          listaCfops.map(cfop => ({
            ...cfop,
            codigoNatureza: `${cfop.codigo} - ${cfop.natureza}`,
          })),
        )
      } catch (error) {
        onError(error)
      }
    }
    fetchListas()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTp])

  const emissorSelecionado = emissores.find(item => item.id === state.emissor_id) || null
  useEmissorUnico({
    visible,
    emissores,
    selecionado: emissorSelecionado,
    onSelecionar: emissor => setState(prevState => ({ ...prevState, emissor_id: emissor.id })),
  })
  const cfopSelecionado = cfops.find(item => item.id === state.cfop_id) || null
  const ufEmissor = emissorSelecionado ? emissorSelecionado.uf : null

  useEffect(() => {
    if (emissorSelecionado) definirFim(emissorSelecionado.uf, emissorSelecionado.city_id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emissorSelecionado, form.ufs])

  const destinatarioFixo = emissorSelecionado
    ? {
        nome: 'DIVERSOS',
        descricao: emissorSelecionado.razao_social,
        cnpj: emissorSelecionado.cnpj,
      }
    : { nome: 'DIVERSOS', descricao: 'selecione o Emissor' }

  const validar = () => {
    if (!deliveriesIds || deliveriesIds.length < 5) {
      return 'Um CTe Globalizado deve conter no mínimo 5 destinatários'
    }
    if (!emissorSelecionado) return 'O campo Emissor é obrigatório'
    const erroOrdemColeta = erroNumeroOrdemColeta(state.numero_ordem_coleta)
    if (erroOrdemColeta) return erroOrdemColeta
    const erroFormulario = form.validar({ cfop: cfopSelecionado, ufEmissor })
    if (erroFormulario) return erroFormulario
    const erroFiscal = erroValoresFiscais(state)
    if (erroFiscal) return erroFiscal
    if (state.observacoes && state.observacoes.length > 2000) {
      return 'O campo Observações deve ter no máximo 2000 caracteres'
    }
    return null
  }

  // Events
  const onBtnSalvarClicked = async () => {
    try {
      const erro = validar()
      if (erro) throw new Error(erro)

      setIsLoading(true)

      const componentesValor = montarComponentesValor(componentes)

      await postEmitCTeGlobalizado({
        ...state,
        deliveries_ids: deliveriesIds,
        ...form.montarPayload(),
        ...(componentesValor.length ? { componentes_valor: componentesValor } : {}),
      })

      onModalSave()
    } catch (error) {
      onError(error)
    } finally {
      setIsLoading(false)
    }
  }

  // Render
  return (
    <CustomModal
      isVisible={visible}
      header={'Emitir CTe Globalizado'}
      width={900}
      onClose={onModalClose}
      contentStyle={{ maxHeight: '80vh', overflowY: 'auto' }}
    >
      <form className="p-grid" onSubmit={e => e.preventDefault()}>
        <div className="p-col-12">
          <div
            style={{
              backgroundColor: '#e3f2fd',
              padding: '10px 15px',
              borderRadius: '5px',
              marginBottom: '10px',
            }}
          >
            <strong>Entregas selecionadas:</strong> {(deliveriesIds || []).length}
          </div>
        </div>

        <div className="p-md-6 p-col-12">
          <CustomDropdown
            optionLabel="razao_social"
            options={emissores}
            value={emissorSelecionado}
            label="Emissor:*"
            filter={true}
            onChange={value => setState(prevState => ({ ...prevState, emissor_id: value.id }))}
          />
        </div>
        <div className="p-md-6 p-col-12">
          <InputNumeroInteiro
            label="Nº Ordem Coleta:*"
            marginTop={10}
            value={state.numero_ordem_coleta}
            onChange={value => setState({ ...state, numero_ordem_coleta: value })}
          />
          <span style={{ color: 'gray', fontSize: 12 }}>
            Número gerado pela transportadora associado a ordem de coleta
          </span>
        </div>

        <SecaoPartes
          form={form}
          selectedTp={selectedTp}
          onError={onError}
          exibirRecebedor={false}
          destinatarioFixo={destinatarioFixo}
          dicaDestinatarioFixo="No CTe Globalizado o destinatário é o próprio emissor, com o nome DIVERSOS (regra da SEFAZ para clientes diversos); o fim da prestação é a cidade do emissor."
        />
        <SecaoTomador
          form={form}
          selectedTp={selectedTp}
          onError={onError}
          somenteLeitura={true}
          dica="No CTe Globalizado o tomador do serviço é sempre o Remetente (regra da SEFAZ)."
        />
        <SecaoPrestacao
          form={form}
          cfops={cfops}
          cfopSelecionado={cfopSelecionado}
          onCfopChange={cfop =>
            setState(prevState => ({ ...prevState, cfop_id: cfop ? cfop.id : null }))
          }
          ufEmissor={ufEmissor}
        />
        <SecaoCarga
          carga={form.carga}
          onChange={form.setCarga}
          onUnidadeChange={form.onUnidadeChange}
          dica="Pré-preenchida com a soma das notas selecionadas; ajuste se necessário."
        />
        <SecaoDocumentosCarga documentos={documentos} carregando={carregandoDados} />

        <ValoresFiscaisCte
          key={visible ? 'aberto' : 'fechado'}
          state={state}
          onStateChange={setState}
          componentes={componentes}
          onComponentesChange={setComponentes}
          onError={onError}
          componentesHint="Nome do componente: máximo de 15 caracteres (limite da SEFAZ). Valores consolidados por regra entre todas as notas selecionadas."
        />

        <div className="p-col-12">
          <CustomInputTextArea
            label="Observações"
            marginTop={10}
            rows={4}
            value={state.observacoes || ''}
            onChange={value => setState({ ...state, observacoes: value })}
          />
        </div>

        <div className="p-col-12" style={{ display: 'flex', justifyContent: 'end' }}>
          <Btn onClick={onBtnSalvarClicked} isLoading={isLoading}>
            Salvar
          </Btn>
        </div>
      </form>
    </CustomModal>
  )
}
